/**
 * "N photos pending" banner + drawer.
 *
 * Renders nothing when the outbox is empty. When there are queued photos
 * (waiting because upload failed / offline / auth was stale), shows a
 * compact banner that opens a drawer listing each with:
 *   - Thumbnail (from the persisted Blob)
 *   - Retry upload
 *   - Save to phone (native Share sheet → Save to Photos)
 *   - Delete
 *
 * Mount once, near the app root.
 */
import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ImageIcon, Loader2, RefreshCw, Share2, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import {
  flushOne,
  removePhoto,
  savePhotoToDevice,
  type PhotoOutboxEntry,
} from '@/lib/offline/photo-outbox'
import { usePendingPhotos, usePendingPhotoCount } from '@/hooks/use-photo-outbox'

export function QueuedPhotosBanner() {
  const count = usePendingPhotoCount()
  const [open, setOpen] = useState(false)

  if (count === 0) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`${count} pending photo${count === 1 ? '' : 's'}`}
        className="fixed left-1/2 -translate-x-1/2 bottom-20 lg:bottom-6 z-40 flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs font-medium shadow-md hover:bg-amber-100 active:bg-amber-200"
      >
        <AlertTriangle className="h-3.5 w-3.5" />
        {count} photo{count === 1 ? '' : 's'} pending upload
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[85vh] rounded-t-2xl border-x-0 border-b-0 p-0"
        >
          <div className="flex h-full max-h-[85vh] flex-col">
            <SheetHeader className="border-b px-4 pt-5 pb-3 text-left">
              <SheetTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Queued photos
              </SheetTitle>
              <SheetDescription>
                These photos are stored on this device and will keep retrying automatically. If they can't upload, tap Save to phone to keep a copy in your Camera Roll.
              </SheetDescription>
            </SheetHeader>
            <QueuedPhotoList />
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}

function QueuedPhotoList() {
  const { entries } = usePendingPhotos()

  if (entries.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center px-6 py-12 text-sm text-muted-foreground">
        All caught up. No photos waiting.
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto divide-y">
      {entries.map((entry) => (
        <QueuedPhotoRow key={entry.id} entry={entry} />
      ))}
    </div>
  )
}

function QueuedPhotoRow({ entry }: { entry: PhotoOutboxEntry }) {
  const [busy, setBusy] = useState<'retry' | 'save' | 'delete' | null>(null)
  const [error, setError] = useState<string | null>(entry.lastError ?? null)

  // Show a persistent Blob URL for the thumbnail. Revoked on unmount.
  const previewUrl = useMemo(() => {
    try {
      return URL.createObjectURL(entry.blob)
    } catch {
      return null
    }
  }, [entry.blob])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const handleRetry = async () => {
    setBusy('retry')
    setError(null)
    try {
      const result = await flushOne(entry.id)
      if (!result.ok) setError(result.error)
    } finally {
      setBusy(null)
    }
  }

  const handleSave = async () => {
    setBusy('save')
    try {
      await savePhotoToDevice(entry.blob, entry.fileName || 'photo.jpg')
    } finally {
      setBusy(null)
    }
  }

  const handleDelete = async () => {
    if (!confirm('Delete this queued photo? This cannot be undone.')) return
    setBusy('delete')
    try {
      await removePhoto(entry.id)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex items-start gap-3 p-3">
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border bg-muted">
        {previewUrl && (
          <img
            src={previewUrl}
            alt=""
            className="h-full w-full object-cover"
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">
          {entry.label ?? entry.fileName}
        </div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          Queued {relativeTime(entry.queuedAt)} · {entry.attempts} attempt
          {entry.attempts === 1 ? '' : 's'}
        </div>
        {error && (
          <div className="mt-1 text-[11px] text-amber-700 line-clamp-2 break-words">
            {error}
          </div>
        )}
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            onClick={handleRetry}
            disabled={busy !== null}
          >
            {busy === 'retry' ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            Retry
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            onClick={handleSave}
            disabled={busy !== null}
          >
            <Share2 className="h-3 w-3" />
            Save to phone
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 gap-1 text-xs text-muted-foreground hover:text-destructive"
            onClick={handleDelete}
            disabled={busy !== null}
          >
            {busy === 'delete' ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Trash2 className="h-3 w-3" />
            )}
            Delete
          </Button>
        </div>
      </div>
    </div>
  )
}

function relativeTime(ts: number): string {
  const ms = Date.now() - ts
  const min = Math.round(ms / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const d = Math.round(hr / 24)
  return `${d}d ago`
}
